import { InputStyled } from '@components/Common/SharedStyles';
import React, { useState } from 'react';
import ReactDatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker-cssmodules.css';
import styled from 'styled-components';
import { fi } from 'date-fns/locale';
import { getYear, getMonth, getDate } from 'date-fns';

registerLocale('fi', fi);

const PickerFix = styled.div<{ $hasError?: boolean }>`
  .react-datepicker__time-container,
  .react-datepicker__time-container .react-datepicker__time-box {
    width: 120px !important;
  }

  .react-datepicker__time-list .react-datepicker__time-list-item {
    height: auto !important;
    padding-top: 7px !important;
    padding-bottom: 7px !important;
  }

  input {
    ${props => props.$hasError && 'border-color: #d32f2f !important;'}
  }
`;

const StyledTimePicker = styled(ReactDatePicker)`
  ${InputStyled};
`;

// Parse time string in format "HH:mm" or "HH.mm" (Finnish format)
const parseTimeString = (value: string): { hours: number; minutes: number } | null => {
  if (!value || !value.trim()) return null;

  // Normalize Finnish time separator
  const normalized = value.trim().replace('.', ':');
  const match = normalized.match(/^(\d{1,2}):(\d{2})$/);

  if (!match) return null;

  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);

  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;

  return { hours, minutes };
};

export const TimePicker: React.FC<any> = props => {
  const [hasError, setHasError] = useState(false);

  // Create a date with the specified time, using referenceDate for the date part
  const createDateWithTime = (hours: number, minutes: number): Date => {
    // Use referenceDate (from DateTimePicker) as the base for the date part
    // This is already in local timezone
    const baseDate = props.referenceDate || props.selected || new Date();

    // Create a new date with the same year/month/day but new hours/minutes
    return new Date(
      getYear(baseDate),
      getMonth(baseDate),
      getDate(baseDate),
      hours,
      minutes,
      0,
      0
    );
  };

  // Handle blur - validate and apply manually typed time with "." separator
  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const value = event.target.value;

    // Empty value is valid (user cleared the field)
    if (!value || !value.trim()) {
      setHasError(false);
      return;
    }

    // Only intervene if value contains "." (Finnish format)
    if (value.includes('.')) {
      const parsed = parseTimeString(value);
      if (parsed && props.onChange) {
        setHasError(false);
        const newDate = createDateWithTime(parsed.hours, parsed.minutes);
        props.onChange(newDate);
      } else if (!parsed) {
        setHasError(true);
      }
    } else {
      // Standard ":" format - validate but let react-datepicker handle it
      const parsed = parseTimeString(value);
      setHasError(!parsed);
    }
  };

  // Handle Enter key - validate and apply manually typed time
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      const input = event.target as HTMLInputElement;
      const value = input.value;

      if (!value || !value.trim()) {
        setHasError(false);
        return;
      }

      const parsed = parseTimeString(value);
      if (parsed && props.onChange) {
        event.preventDefault();
        setHasError(false);
        const newDate = createDateWithTime(parsed.hours, parsed.minutes);
        props.onChange(newDate);
        input.blur();
      } else {
        event.preventDefault();
        setHasError(true);
      }
    }
  };

  // Handle selection from dropdown or standard input
  const handleChange = (date: Date | null) => {
    setHasError(false);
    if (props.onChange) {
      props.onChange(date);
    }
  };

  // Destructure props we handle ourselves to avoid passing them to ReactDatePicker
  const { referenceDate, onChange, ...pickerProps } = props;

  return (
    <PickerFix $hasError={hasError}>
      <StyledTimePicker
        disabled={props.isDisabled}
        isClearable={props.isClearable}
        showTimeSelect
        showTimeSelectOnly
        timeFormat="HH:mm"
        dateFormat="HH:mm"
        locale="fi"
        timeIntervals={30}
        autoComplete="off"
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onChange={handleChange}
        id={props.name}
        {...pickerProps}
      >
        {props.children}
      </StyledTimePicker>
    </PickerFix>
  );
};

export default TimePicker;
